import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Card,
  Typography,
  Grid2,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import AppButton from "../../../components/CommonReusableComponent/AppButton/AppButton";
import { Link } from "react-router-dom";
import Dropzone from "react-dropzone";
import deleteIcon from "../../../assets/images/Delete.svg";
import Selected_File from "../../../assets/images/File.svg";
import { Icons } from "../../../config/MuiIcons.config";
import { AppTheme } from "../../../theme/App.theme";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import { useBackdrop } from "../../../contexts/BackDrop.context";
import { createAuditTypeWithBulkActivities } from "../../../helpers/api-functions-helper";
import {
  APP_MESSAGE,
  FORM_VALIDATIONS,
  SNACKBAR_VARIANT,
  USE_FORMIK_SCHEMA,
} from "../../../config/Messages.config";
import {
  getFileSize,
  exportToExcel,
  convertActivityNameExcelToJson,
  generateRandomId,
} from "../../../common/CommonFunctions";
import {
  sampleActivityColumnFormat,
  sampleActivityDataFormat,
} from "./AuditTypeMaster.config";
import { getUserRoleAndScreens } from "../../../services/authServices/auth.services";
import MUIErrorText from "../../../components/MUIErrorText/MUIErrorText";
import AppInputField from "../../../components/CommonReusableComponent/AppInputField/AppInputField";
import AppAutoCompleteField from "../../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";

const AuditTypeBulkUploadModal = ({
  ModalState,
  ToggleFunction,
  FetchAllAuditTypes = () => {},
  AuditTypeCategories = [],
  ModalType = "CREATE", // Default to 'CREATE' if not provided
  UpdateParentData = () => {},
}) => {
  const { showSnackbar } = useSnackbar();
  const { accountId, userUid } = getUserRoleAndScreens();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  // Modal type
  const isUpdateMode = ModalType === "UPDATE" ? true : false;
  // Initial file configuration
  const initialFileConfig = {
    name: "",
    size: "0 MB",
  };
  const [fileNameAndSize, setFileNameAndSize] = useState({
    ...initialFileConfig,
  });
  const [convertedExcelToJsonData, setConvertedExcelToJsonData] = useState([]);

  // Checkbox state to keep existing activities
  const [keepExistingActivities, setKeepExistingActivities] = useState(true);

  // Function to toggle checkbox state
  const toggleKeepExistingActivities = (event) => {
    const { checked } = event.target;
    setKeepExistingActivities(checked);
  };

  // Function to create bulk holidays
  const uploadBulkActivtiesToCreateAuditType = (Data) => {
    showBackdrop();
    createAuditTypeWithBulkActivities(Data)
      .then((response) => {
        if (response && response.data) {
          // console.log(response.data);
          const { statusCode, success, data, message } = response.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              data ||
                message ||
                APP_MESSAGE.AUDIT_TYPE_MASTER.BULK_UPLOAD_ACTIVITIES.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            showSnackbar(
              APP_MESSAGE.AUDIT_TYPE_MASTER.BULK_UPLOAD_ACTIVITIES.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            FetchAllAuditTypes();
            ToggleFunction();
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.AUDIT_TYPE_MASTER.BULK_UPLOAD_ACTIVITIES.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        hideBackdrop();
      });
  };

  // Function to convert array of activity names to an object with id
  const objectifyData = (data) => {
    const reqData = [...data].map((item) => {
      return {
        name: item,
        id: generateRandomId(15),
      }
    })

    return reqData;
  }

  // Function to convert uploaded excel file to JSON data
  const handleSelectedFile = async (acceptedFile) => {
    if (acceptedFile && acceptedFile[0]) {
      try {
        const { name, size } = acceptedFile[0];
        setFileNameAndSize({
          name,
          size: getFileSize(size),
        });
        const jsonData = await convertActivityNameExcelToJson(acceptedFile[0]);
        // console.log("jsonData: ", jsonData);
        if (isUpdateMode) {
          const updatedData = objectifyData(jsonData);
          setConvertedExcelToJsonData(updatedData);
        }
        setConvertedExcelToJsonData(jsonData);
      } catch (error) {
        console.error("Error: ", error.message);
        showSnackbar(
          error.message ||
            APP_MESSAGE.AUDIT_TYPE_MASTER.BULK_UPLOAD_ACTIVITIES
              .INVALID_FILE_FORMAT,
          SNACKBAR_VARIANT.ERROR
        );
        setConvertedExcelToJsonData([]);
      }
    } else {
      showSnackbar(
        APP_MESSAGE.AUDIT_TYPE_MASTER.BULK_UPLOAD_ACTIVITIES.REQUIRED_FILE,
        SNACKBAR_VARIANT.ERROR
      );
    }
  };

  // Function to delete uploaded file from dropzone
  const handleDelete = () => {
    setConvertedExcelToJsonData([]);
  };

  // Function to handle form reset after update
  const handleFormReset = () => {
    setConvertedExcelToJsonData([]);
    setKeepExistingActivities(true);
    ToggleFunction();
  } 

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      abbreviation: "",
      auditTypeCategoryId: ""
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        // .matches(
        //   FORM_VALIDATIONS.TEXT_INPUT.REGEX.INITIAL,
        //   FORM_VALIDATIONS.TEXT_INPUT.MESAAGE.INITIAL
        // )
        .required(USE_FORMIK_SCHEMA.AUDIT_TYPE_MASTER.AUDIT_NAME),
      abbreviation: Yup.string()
        .trim()
        .matches(
          FORM_VALIDATIONS.LOCATION_NAME_PERFIX.REGEX,
          FORM_VALIDATIONS.LOCATION_NAME_PERFIX.MESSAGE
        )
        .required(USE_FORMIK_SCHEMA.AUDIT_TYPE_MASTER.ABBRIVATIONS),
      auditTypeCategoryId: Yup.string()
        .required(USE_FORMIK_SCHEMA.AUDIT_TYPE_MASTER.AUDIT_TYPE_CATEGORY),
    }),
    onSubmit: (values) => {
      if (!convertedExcelToJsonData[0]) {
        showSnackbar(
          APP_MESSAGE.AUDIT_TYPE_MASTER.BULK_UPLOAD_ACTIVITIES.REQUIRED_FILE,
          SNACKBAR_VARIANT.ERROR
        );
      } else {
        const payload = {
          ...values,
          activityList: [...convertedExcelToJsonData],
          accountId: accountId,
          createdBy: userUid,
          updatedBy: userUid,
        };
        // console.log("payload: ", payload)
        uploadBulkActivtiesToCreateAuditType(payload);
      }
    },
    onReset: () => {
      handleFormReset();
    },
  });

  return (
    <Dialog
      open={ModalState}
      aria-labelledby="form-dialog-title"
      maxWidth="md"
      fullWidth
      slotProps={{
        style: {
          opacity: 0.7,
        },
      }}
    >
      <DialogTitle id="responsive-dialog-title">
        <div className="d-flex align-items-center">
          <Typography
            className="inter-medium-500"
            style={{
              fontSize: "18px",
            }}
          >
            {/* Audit Type Creation with Bulk Activities */}
            {ModalType === "CREATE"
              ? "Create Audit Type By Uploading Bulk Activities"
              : "Update Audit Type By Uploading Bulk Activities"}
          </Typography>
          <div className="ms-auto"></div>
          <IconButton
            aria-label="close"
            onClick={() => handleFormReset()}
            style={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icons.Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <div>
          <Grid2 container spacing={1}>
            <Grid2 size={12}>
              <div className="mb-1">
                <Typography variant="caption" className="inter-regular-400">
                  Download the standard excel file, update it, save your
                  changes, and re-upload.
                </Typography>
              </div>
              <div className="d-grid d-md-flex justify-content-md-between">
              <div>
                <Link
                  href="#"
                  onClick={() => {
                    exportToExcel(
                      sampleActivityDataFormat,
                      sampleActivityColumnFormat,
                      "Sample_Activity_Bulk_Upload"
                    );
                  }}
                  style={{
                    color: AppTheme.PRIMARY_COLOR,
                    fontSize: "15px",
                  }}
                >
                  Download Standard Format
                </Link>{" "}
                <Icons.FileDownloadIcon
                  onClick={() => {
                    exportToExcel(
                      sampleActivityDataFormat,
                      sampleActivityColumnFormat,
                      "Sample_Activity_Bulk_Upload"
                    );
                  }}
                  sx={{
                    color: AppTheme.PRIMARY_COLOR,
                    cursor: "pointer",
                    fontSize: 20,
                  }}
                />
              </div>
              
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={keepExistingActivities}
                      onChange={toggleKeepExistingActivities}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={
                    <Typography
                      className="inter-regular-400"
                      style={{ fontSize: "15px" }}
                    >
                      Keep Existing Activities
                    </Typography>
                  }
                />
              </div>
            </Grid2>
            {!isUpdateMode ? (
              <>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                  <Typography
                    variant="body2"
                    className="inter-regular-400 py-2"
                  >
                    Audit Type Name
                  </Typography>
                  <AppInputField
                    Name={"name"}
                    Id={"name"}
                    Type={"text"}
                    Value={validation.values.name || ""}
                    OnBlur={validation.handleBlur}
                    IsCapitalizeRequired={true}
                    OnChange={(e) => {
                      validation.handleChange(e);
                    }}
                    PlaceHolder={"Enter name"}
                    Error={
                      validation.touched.name && validation.errors.name
                        ? true
                        : false
                    }
                    HelperText={
                      validation.touched.name && validation.errors.name ? (
                        <MUIErrorText ErrorMessage={validation.errors.name} />
                      ) : null
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                  <Typography
                    variant="body2"
                    className="inter-regular-400 py-2"
                  >
                    Audit Type Category
                  </Typography>
                  <AppAutoCompleteField
                    TextInputName={"auditTypeCategoryId"}
                    TextInputId={"auditTypeCategoryId"}
                    Id={"auditTypeCategoryId"}
                    TextInputPlaceholder={"Select audit type category"}
                    IsMultiple={false}
                    Options={AuditTypeCategories}
                    SortOptions={true}
                    HandleOnBlur={validation.handleBlur}
                    HandleOnChange={(event, obj) => {
                      const ID = (obj && obj?.value) || "";
                      const ev = {
                        ...event,
                        target: {
                          ...event?.target,
                          name: "auditTypeCategoryId",
                          value: ID,
                        },
                      };
                      validation.handleChange(ev);
                    }}
                    Value={validation.values.auditTypeCategoryId}
                    TextInputError={
                      validation.touched.auditTypeCategoryId &&
                      validation.errors.auditTypeCategoryId
                        ? true
                        : false
                    }
                    TextInputHelperText={
                      validation.touched.auditTypeCategoryId &&
                      validation.errors.auditTypeCategoryId
                        ? validation.errors.auditTypeCategoryId
                        : null
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                  <Typography
                    variant="body2"
                    className="inter-regular-400 py-2"
                  >
                    Unique Prefix
                  </Typography>
                  <AppInputField
                    Name={"abbreviation"}
                    Id={"abbreviation"}
                    Type={"text"}
                    Value={validation.values.abbreviation || ""}
                    OnBlur={validation.handleBlur}
                    OnChange={(event) => {
                      const { value, name } = event.target;
                      validation.handleChange({
                        ...event,
                        target: {
                          ...event.target,
                          name: name,
                          value: value.toUpperCase(),
                        },
                      });
                    }}
                    PlaceHolder={"Enter unique prefix"}
                    Error={
                      validation.touched.abbreviation &&
                      validation.errors.abbreviation
                        ? true
                        : false
                    }
                    HelperText={
                      validation.touched.abbreviation &&
                      validation.errors.abbreviation ? (
                        <MUIErrorText
                          ErrorMessage={validation.errors.abbreviation}
                        />
                      ) : null
                    }
                  />
                </Grid2>
              </>
            ) : null}

            {!convertedExcelToJsonData[0] ? (
              <Grid2 size={12}>
                <Typography
                  className="inter-regular-400 mb-1"
                  style={{ fontSize: "15px" }}
                >
                  Select file to upload
                </Typography>
                <Dropzone
                  onDrop={handleSelectedFile}
                  maxSize={5 * 1024 * 1024}
                  accept={{
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                      [".xlsx"],
                    "application/vnd.ms-excel": [".xls"],
                  }}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <>
                      <div
                        style={{
                          height: "150px",
                          border: `2px dashed ${AppTheme.PRIMARY_COLOR}`,
                          borderRadius: "6px",
                          marginBottom: "2px",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          {...getRootProps()}
                        >
                          <div>
                            <input {...getInputProps()} />
                            <Icons.CloudUploadIcon
                              sx={{
                                fontSize: 40,
                                color: `${AppTheme.PRIMARY_COLOR}`,
                              }}
                            />
                            {isDragActive ? (
                              <Typography
                                variant="subtitle2"
                                className="inter-regular-400 my-1"
                              >
                                Drop your file here
                              </Typography>
                            ) : (
                              <>
                                <Typography
                                  variant="subtitle2"
                                  className="inter-regular-400 my-1"
                                >
                                  Drag your file here or{" "}
                                  <Link
                                    href="#"
                                    sx={{
                                      textDecoration: "none",
                                    }}
                                  >
                                    browse
                                  </Link>
                                </Typography>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Dropzone>
                <Typography variant="caption" className="inter-regular-400">
                  Note: Please follow standard format to avoid data
                  discrepancies. (Supports only .xls, .xlsx)
                </Typography>
              </Grid2>
            ) : (
              <Grid2 size={12}>
                <Card
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px",
                    bgcolor: "#f9f9f9",
                    borderRadius: "4px",
                    width: "100%",
                    boxShadow: "none",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={Selected_File}
                      alt={"File Icon"}
                      style={{ width: "55px", height: "55px" }}
                    />
                    <div className="d-grid ms-2">
                      <Typography
                        variant="subtitle2"
                        noWrap={true}
                        className="inter-regular-400"
                      >
                        {fileNameAndSize?.name}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        className="inter-regular-400"
                      >
                        {fileNameAndSize?.size}
                      </Typography>
                    </div>
                  </div>
                  <img
                    src={deleteIcon}
                    onClick={handleDelete}
                    className="cursor-pointer"
                    alt={"Delete File"}
                    style={{ width: "22px", height: "22px" }}
                  />
                </Card>
              </Grid2>
            )}
            {/* {isUpdateMode && (
              <Grid2 size={12} display={"flex"} justifyContent={"flex-end"}>
                
              </Grid2>
            )} */}
          </Grid2>
          {convertedExcelToJsonData[0] && (
            <Grid2 container spacing={1} justifyContent={"right"}>
              <Grid2 size={{ xs: 12, sm: 3, md: 2 }} className="my-2">
                <AppButton
                  OnPress={() => {
                    if (!isUpdateMode) {
                      validation.handleSubmit();
                    } else {
                      UpdateParentData(
                        convertedExcelToJsonData,
                        keepExistingActivities
                      );
                      handleFormReset();
                    }
                  }}
                >
                  Upload
                </AppButton>
              </Grid2>
            </Grid2>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuditTypeBulkUploadModal;
